import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { FormInput, FormSelect, ErrorMessage } from './single-inputs';
import {
  trackCheckoutFieldFocus,
  trackCheckoutFieldBlur,
} from "../utils/checkout-field-tracking";
import { checkName } from './utils';
import { isValidEmail } from '../utils/email-verification';
import { trackError } from '../utils/error-tracking';
import { trackFunnelEvent } from '../utils/track-funnel';
import { isAutofillInput } from '../utils/address-autofill';
import { typedAddressShape } from '../utils/typed-address-shape';
import usStateNames from '../data/js/states';
import { US_STATE_CODES } from '../utils/sales-tax/us-state-table';
import { CA_PROVINCE_CODES } from '../utils/sales-tax/ca-province-table';
import type { GuestShippingAddress } from '../schemas/components/guest-address-autocomplete';
import type { ResolvedGuestAddress } from '../schemas/api/checkout/geocode-address';
import { geocodeShippingAddress } from '../utils/geocode-shipping-address';

// Timeout/delay constants
const AUTOFILL_DETECTION_DELAY_MS = 100; // Delay to detect browser autofill completion
const AUTOCOMPLETE_INIT_RETRY_DELAY_MS = 500; // Retry delay for Google Maps API initialization
const INPUT_FOCUS_DELAY_MS = 100; // Delay before focusing input element
// Clicking a Google suggestion blurs the input before `place_changed` fires, so
// the blur-commit below waits long enough for that to land before it decides the
// buyer left without picking anything.
const BLUR_COMMIT_DELAY_MS = 350;

// Address validation constants
const MIN_ADDRESS_LENGTH = 10; // Minimum length to detect autofill vs. manual typing
/**
 * US and Canada, which is everywhere Sellwild ships.
 *
 * The manual fields used to be US-only in three separate places -- the state
 * list, the postcode pattern, and a hard-coded `country: 'US'` on commit -- so
 * a Canadian buyer whose address the geocoders could not place had no way to
 * enter it at all. The tax tables already knew about both countries
 * (utils/sales-tax/ca-province-table.ts); only this form did not.
 */
export const SHIPPING_COUNTRIES = ['US', 'CA'] as const;
type ShippingCountry = (typeof SHIPPING_COUNTRIES)[number];

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
];

/** `A1A 1A1`, with the space optional and a hyphen tolerated. */
const CA_POSTAL_PATTERN = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export function isShippingCountry(value: string): value is ShippingCountry {
  return (SHIPPING_COUNTRIES as readonly string[]).includes(value);
}

/** The regions a country has, for the select and for validation. */
export function regionCodesFor(country: string): readonly string[] {
  return country === 'CA' ? CA_PROVINCE_CODES : US_STATE_CODES;
}

export function postalIsValid(country: string, postal: string): boolean {
  return country === 'CA' ? CA_POSTAL_PATTERN.test(postal) : ZIP_PATTERN.test(postal);
}

export function regionOptionsFor(country: string) {
  return regionCodesFor(country).map((code) => ({
    id: code,
    name: (usStateNames as Record<string, string>)[code] ?? code,
  }));
}

/**
 * Whether the Maps geocoder exists.
 *
 * `window.google` alone proves nothing: other Google scripts define it before
 * Maps loads, which is how `new window.google.maps.Geocoder()` threw
 * "reading 'Geocoder'" on a guest checkout and dropped the typed address.
 */
const isGeocoderReady = () => typeof window.google?.maps?.Geocoder === 'function';

type GoogleAddressComponent = { types: string[]; long_name: string; short_name: string };

/**
 * The street-address fields from a Google place or geocode result.
 *
 * No `line2`: Google returns no subpremise here, so carrying one would only
 * blank out an apt line the buyer already typed when they re-pick an address.
 */
function addressFromComponents(components: GoogleAddressComponent[]): ResolvedGuestAddress {
  const address = { line1: '', city: '', state: '', postal_code: '', country: 'US' };
  for (const component of components) {
    switch (component.types[0]) {
      case 'street_number':
        address.line1 = `${component.long_name} ${address.line1}`;
        break;
      case 'route':
        address.line1 += component.short_name;
        break;
      case 'locality':
        address.city = component.long_name;
        break;
      case 'administrative_area_level_1':
        address.state = component.short_name;
        break;
      case 'postal_code':
        address.postal_code = component.long_name;
        break;
      case 'country':
        address.country = component.short_name;
        break;
    }
  }
  return { ...address, line1: address.line1.trim() };
}

type ManualField =
  | 'manualLine1'
  | 'manualCity'
  | 'manualState'
  | 'manualZip'
  | 'manualCountry';
type ManualFields = Record<ManualField, string>;

const EMPTY_MANUAL_FIELDS: ManualFields = {
  manualLine1: '', manualCity: '', manualState: '', manualZip: '',
  // Pre-selected rather than blank: the overwhelming majority of buyers are in
  // the US, and an empty required select is one more thing to fill in.
  manualCountry: 'US',
};

/** What is wrong with each manual field: '' when fine, else a `getClassForState` key. */
function manualFieldErrors(fields: ManualFields): ManualFields {
  return {
    manualLine1: fields.manualLine1.trim() ? '' : 'empty',
    manualCity: fields.manualCity.trim() ? '' : 'empty',
    manualState: regionCodesFor(fields.manualCountry).includes(fields.manualState) ? '' : 'empty',
    manualZip: postalIsValid(fields.manualCountry, fields.manualZip.trim())
      ? ''
      : fields.manualZip.trim() ? 'invalid' : 'empty',
    manualCountry: isShippingCountry(fields.manualCountry) ? '' : 'empty',
  };
}

// Google Maps types declared in global.d.ts

/**
 * Always a guest, so the tracking context is constant. The sell flow renders
 * this too, with `tracksCheckout` off, so it sends none of these.
 */
const GUEST_FIELD_CONTEXT = { loggedIn: false };

/**
 * How an address got into the form: a Google suggestion, typed text a geocoder
 * placed, the browser's autofill (into the lookup or the fields), or the
 * fields by hand. `address_entered` only said the address was complete, so a
 * week of autofill complaints had no way to show up in the funnel.
 */
type AddressSource = 'suggestion' | 'typed_geocode' | 'typed_server' | 'autofill_lookup' | 'autofill_fields' | 'manual';

function trackAddressEvent(event: string, data: Record<string, unknown>) {
  trackFunnelEvent('checkout', event, data, { component: 'GuestAddressAutocomplete' });
}

interface AddressErrors {
  [key: string]: string;
}

interface GuestAddressAutocompleteProps {
  shippingAddress: GuestShippingAddress;
  onAddressChange: (address: GuestShippingAddress) => void;
  /** Called once an address is committed. Checkout has nothing to do then; the sell flow does. */
  onAddressComplete?: () => void;
  /**
   * Resolves typed text on the server when the Maps script is not available.
   * Without it, or when it answers null, the buyer is asked for the fields.
   */
  resolveTypedAddress?: (text: string) => Promise<ResolvedGuestAddress | null>;
  errors?: AddressErrors;
  setErrors?: (errors: AddressErrors) => void;
  nameLabel: string;
  addressLabel: string;
  /**
   * The email field, rendered under the name in the Contact block, after the
   * address.
   *
   * Passed in rather than built here: its state lives in checkout-shared, and a
   * signed-in shopper with no email on file is asked for one on a page where
   * this component never renders. Null when there is nothing to ask.
   */
  emailField?: React.ReactNode;
  /**
   * What is typed in `emailField`. With a name, it folds the Contact block to
   * one line once the buyer has moved on -- see `contactFolded`.
   */
  contactEmail?: string;
  /**
   * The one-step checkout's order (owner, 2026-10-25: Shop's order): the name
   * as the first field of Delivery, above the address, and no Contact block,
   * since the page asks for the email in its own Contact section above.
   */
  nameFirst?: boolean;
  /**
   * Where the page wants the name drawn: its Contact section, above the email
   * (owner, 2026-11-03). The field and its state stay here, so the name still
   * lands on the shipping address; only where it is drawn moves. With
   * `nameFirst` and no slot (yet), it is the first field of Delivery.
   */
  nameSlot?: HTMLElement | null;
  nameInvalidErrorMessage?: string;
  lastNameNeededErrorMessage?: string;
  addressMissingErrorMessage?: string;
  /**
   * Send the checkout funnel's address and name events. On by default; the
   * guest sell flow (/sell) turns it off. Left on there, a seller typing their
   * ship-from address read as a buyer in checkout: 3 fake checkout stories in
   * the week to 2026-11-03, each "entered an address" on /sell.
   */
  tracksCheckout?: boolean;
  /**
   * Put the cursor in the address box when it first shows. Off by default:
   * on checkout it opened the phone keyboard over the page before the buyer
   * had read it (owner, 2026-11-03). The sell flow turns it on, since the box
   * shows there only after the seller asks for it. Reopening the box (Change,
   * or closing the type-it-in form) still focuses it either way.
   */
  autoFocusOnOpen?: boolean;
}

export interface GuestAddressAutocompleteHandle {
  validate: () => boolean;
  setAddressForTest: (address: Partial<GuestShippingAddress>) => void;
}

const GuestAddressAutocomplete = forwardRef<GuestAddressAutocompleteHandle, GuestAddressAutocompleteProps>(
  (props, ref) => {
    const {
      shippingAddress,
      onAddressChange,
      onAddressComplete = () => {},
      resolveTypedAddress,
      errors: _errors = {},
      setErrors: _setErrors,
      nameLabel,
      addressLabel: _addressLabel,
      emailField = null,
      contactEmail = '',
      nameFirst = false,
      nameSlot = null,
      nameInvalidErrorMessage = "Name must be at least 3 characters",
      lastNameNeededErrorMessage = "Please include your last name",
      addressMissingErrorMessage = "Pick your address from the suggestions",
      tracksCheckout = true,
      autoFocusOnOpen = false,
    } = props;

    // Whether the box's next showing takes the cursor. Set by the buyer's own
    // taps (Change, Use address lookup), never by the page opening.
    const focusOnOpenRef = useRef(autoFocusOnOpen);

    // A ref, so the tracked callbacks below need no new dependency.
    const tracksCheckoutRef = useRef(tracksCheckout);
    tracksCheckoutRef.current = tracksCheckout;

    // Opens on the committed-address card, not an empty search box, when a
    // whole address is already here -- a guest's saved draft coming back on
    // reload (utils/guest-checkout-draft.ts). The card's ✕ reopens the search.
    const [isEditingAddress, setIsEditingAddress] = useState(
      () => !(shippingAddress.line1 && shippingAddress.city && shippingAddress.state && shippingAddress.postal_code),
    );
    const [nameState, setNameState] = useState({ name: shippingAddress.name || '' });
    const [nameFocus, setNameFocus] = useState({ name: false });
    const [nameFilled, setNameFilled] = useState({ name: !!shippingAddress.name });
    const [nameErrors, setNameErrors] = useState({ name: '' });
    // True while the buyer is in the Contact fields, or has pressed its Change.
    const [contactActive, setContactActive] = useState(false);
    const contactRef = useRef<HTMLDivElement>(null);
    const [line2State, setLine2State] = useState({ line2: shippingAddress.line2 || '' });
    const [line2Focus, setLine2Focus] = useState({ line2: false });
    const [line2Filled, setLine2Filled] = useState({ line2: !!shippingAddress.line2 });
    const [line2Errors, setLine2Errors] = useState({ line2: '' });
    const [addressFocus, setAddressFocus] = useState(false);
    // Set when the buyer tries to continue with nothing committed in the address
    // field. Without it an incomplete address failed validation in silence and
    // the continue button simply did nothing.
    const [addressError, setAddressError] = useState(false);
    // Street, city, state and ZIP as separate fields, shown when the typed
    // address could not be looked up without the Maps script.
    const [manualEntry, setManualEntry] = useState(false);
    const [manualFields, setManualFields] = useState<ManualFields>(EMPTY_MANUAL_FIELDS);
    const [manualFocus, setManualFocus] = useState<Record<string, boolean>>({});
    const [manualFilled, setManualFilled] = useState<Record<string, boolean>>({});
    const [manualErrors, setManualErrors] = useState<ManualFields>(EMPTY_MANUAL_FIELDS);
    // Blur, Enter and autofill can all ask about the same text at once; one
    // lookup per text is enough, and the server call costs money.
    const resolvingRef = useRef<string | null>(null);

    // Google Maps types are globally declared as untyped
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const autocompleteRef = useRef<any>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const shippingAddressRef = useRef(shippingAddress);
    shippingAddressRef.current = shippingAddress;
    const onAddressChangeRef = useRef(onAddressChange);
    onAddressChangeRef.current = onAddressChange;
    const onAddressCompleteRef = useRef(onAddressComplete);
    onAddressCompleteRef.current = onAddressComplete;

    /**
     * Hand a whole address up to the parent, advancing the ref with it.
     *
     * Every writer here used to spread the `shippingAddress` prop it had closed
     * over. Two of them running in the same commit -- the name and line2 sync
     * effects, which is what autofill and any batched update produce -- spread
     * the same stale address, so the second write dropped the first one's field.
     * Advancing the ref makes the second merge onto the first one's result.
     */
    const pushAddress = useCallback((next: GuestShippingAddress) => {
      shippingAddressRef.current = next;
      onAddressChangeRef.current(next);
    }, []);

    /** Push one field, skipping the write when it already holds that value. */
    const pushAddressField = useCallback((field: 'name' | 'line2', value: string) => {
      const current = shippingAddressRef.current;
      if ((current[field] || '') === value) return;
      pushAddress({ ...current, [field]: value });
    }, [pushAddress]);

    /** Whether the parent holds an address complete enough to ship and quote. */
    const hasCommittedAddress = useCallback(() => {
      const { line1, city, state, postal_code } = shippingAddressRef.current;
      return Boolean(line1 && city && state && postal_code);
    }, []);

    /** Hand a resolved street address up and show it as the selected address. */
    const commitAddress = useCallback((fields: ResolvedGuestAddress, source: AddressSource) => {
      if (tracksCheckoutRef.current) trackAddressEvent('address_committed', { source, country: fields.country });
      pushAddress({ ...shippingAddressRef.current, ...fields });
      setIsEditingAddress(false);
      setManualEntry(false);
      setAddressError(false);
      onAddressCompleteRef.current();
    }, [pushAddress]);

    /**
     * Commit the manual fields if they make a whole address.
     *
     * `showErrors` is for Continue: leaving a field half-way through the form
     * should not light up the fields the buyer hasn't reached yet.
     */
    const commitManualAddress = useCallback((fields: ManualFields, showErrors: boolean, source: AddressSource = 'manual'): boolean => {
      const fieldErrors = manualFieldErrors(fields);
      const complete = Object.values(fieldErrors).every((e) => e === '');
      if (!complete) {
        if (showErrors) {
          setManualErrors(fieldErrors);
          setManualFilled({
            manualLine1: true, manualCity: true, manualState: true,
            manualZip: true, manualCountry: true,
          });
        }
        return false;
      }
      setManualErrors(EMPTY_MANUAL_FIELDS);
      commitAddress({
        line1: fields.manualLine1.trim(),
        city: fields.manualCity.trim(),
        state: fields.manualState,
        postal_code: fields.manualZip.trim(),
        country: fields.manualCountry,
      }, source);
      return true;
    }, [commitAddress]);

    /*
     * A filled field says so. The manual fields have no validator of their own
     * (Continue checks them all at once), and FormInput draws its check only
     * for `valid` -- so a street, city and ZIP the browser had just filled in
     * sat there unticked, looking unfinished. Each field is ticked as soon as
     * it holds something acceptable; an error Continue raised stays until the
     * field is fixed.
     */
    useEffect(() => {
      const fieldErrors = manualFieldErrors(manualFields);
      setManualErrors((current) => {
        const next = { ...current };
        (Object.keys(manualFields) as ManualField[]).forEach((id) => {
          const ok = fieldErrors[id] === '';
          if (ok && manualFields[id].trim()) next[id] = 'valid';
          else if (current[id] === 'valid') next[id] = '';
        });
        return (Object.keys(next) as ManualField[]).every((id) => next[id] === current[id]) ? current : next;
      });
    }, [manualFields]);

    /*
     * Autofill saves the address without a detour through the fields.
     *
     * A buyer picking a saved address from the browser's list fills street,
     * city, state and ZIP at once -- and then nothing happened until they left
     * a field, because the fields commit on blur. The tell is a field changing
     * while the buyer is not in it: typing only ever changes the focused one.
     * The browser writes its fields one event at a time, so the check waits a
     * moment for the rest to land, then commits if the address is whole.
     */
    const manualFieldsRef = useRef(manualFields);
    const lastManualRef = useRef(manualFields);
    const autofillCommitTimer = useRef<number | undefined>(undefined);
    useEffect(() => {
      manualFieldsRef.current = manualFields;
      const prev = lastManualRef.current;
      lastManualRef.current = manualFields;
      if (!manualEntry) return;
      const active = document.activeElement?.id;
      const filledElsewhere = (Object.keys(manualFields) as ManualField[]).some(
        (id) => manualFields[id] !== prev[id] && manualFields[id].trim() !== '' && active !== `${id}-input`,
      );
      if (!filledElsewhere) return;
      // Held in a ref, not an effect cleanup: the browser's last write is often
      // to the focused field, and a cleanup would cancel the check it needs.
      window.clearTimeout(autofillCommitTimer.current);
      autofillCommitTimer.current = window.setTimeout(() => {
        if (!hasCommittedAddress()) commitManualAddress(manualFieldsRef.current, false, 'autofill_fields');
      }, AUTOFILL_DETECTION_DELAY_MS * 2);
    }, [manualFields, manualEntry, hasCommittedAddress, commitManualAddress]);
    useEffect(() => () => window.clearTimeout(autofillCommitTimer.current), []);

    /**
     * Ask for the fields, starting the street line from what was typed before the first comma.
     *
     * `detail` says how a failed lookup got here. Prod, 14 days to 2026-10-28:
     * 18 checkout sessions had the lookup fail, every one a server "not found",
     * 15 of 20 on Android and many within seconds of the page opening -- and
     * the row could not say whether the text was typed, autofilled or left
     * behind on a blur, or whether Maps had loaded at all.
     */
    const openManualEntry = useCallback((typed: string, reason: 'buyer' | 'lookup_failed', detail: Record<string, unknown> = {}) => {
      if (tracksCheckoutRef.current) trackAddressEvent('address_manual_opened', { reason, had_text: typed.trim() !== '', ...detail });
      setManualFields((current) => ({
        ...current,
        manualLine1: current.manualLine1 || typed.split(',')[0].trim(),
      }));
      // The fields render under `isEditingAddress && manualEntry`. Every caller
      // before the link was a failed lookup, which is already an editing state,
      // so setting only the second flag was enough; a buyer pressing "enter
      // manually" from a committed address is not, and got a no-op button.
      setIsEditingAddress(true);
      setManualEntry(true);
      setAddressError(false);
    }, []);

    /**
     * Go back to the lookup.
     *
     * Manual entry used to be a one-way door, because it was only ever reached
     * by failing: there was nothing to go back TO. Now that it is a choice a
     * buyer makes, opening it by mistake left them typing out an address the
     * lookup would have filled in for them. The typed fields are kept rather
     * than cleared -- pressing this is "let me try the lookup again", not
     * "throw away what I typed" -- and nothing is committed, so the parent's
     * address is untouched either way.
     */
    const closeManualEntry = useCallback(() => {
      focusOnOpenRef.current = true;
      setManualEntry(false);
      setManualErrors(EMPTY_MANUAL_FIELDS);
      setIsEditingAddress(true);
      setAddressError(false);
    }, []);

    // Expose validation method and test helpers to parent component
    useImperativeHandle(ref, () => ({
      validate: () => {
        const nameValid = checkName(nameState, nameErrors, setNameErrors, 'name');
        if (!nameValid) {
          setNameFilled({ name: true }); // Ensure error displays
        }
        // The address is validated here as well as in the parent, because this
        // is the only component that can say so on the field itself. Manual
        // fields are committed here too, so Continue works without a separate
        // "use this address" step.
        if (manualEntry && !hasCommittedAddress()) {
          return commitManualAddress(manualFields, true) && nameValid;
        }
        const addressValid = hasCommittedAddress();
        setAddressError(!addressValid);
        return nameValid && addressValid;
      },
      setAddressForTest: (address: Partial<GuestShippingAddress>) => {
        onAddressChange({
          ...shippingAddress,
          ...address,
        });
        setIsEditingAddress(false);
        onAddressComplete();
      }
    }));

    useEffect(() => {
      if (typeof window !== 'undefined' && window.Cypress) {
        window.setGuestAddress = (address: Partial<GuestShippingAddress>) => {
          onAddressChange({
            ...shippingAddress,
            ...address,
          });
          setIsEditingAddress(false);
          onAddressComplete();
        };
        window.setGuestName = (name: string) => {
          setNameState({ name });
          setNameFilled({ name: true });
          setNameErrors({ name: '' });
        };
      }
      return () => {
        if (typeof window !== 'undefined' && window.Cypress) {
          delete window.setGuestAddress;
          delete window.setGuestName;
        }
      };
    }, [onAddressChange, onAddressComplete, shippingAddress]);

    /**
     * Commit an address the buyer typed rather than picked.
     *
     * With Maps loaded, the browser geocoder places it; an address it can't
     * place is left for the buyer to pick from the suggestions Maps is showing.
     * Without Maps there are no suggestions, so the text goes to the server, and
     * if the server can't place it either, the buyer gets separate fields. Maps
     * loads `lazyOnload` and blockers keep it away entirely, so that path used
     * to leave the address uncommitted with nothing to try next.
     *
     * Leaving the box (`blur`) never opens the fields while Maps is loaded.
     * On a phone, closing the keyboard is a blur: a buyer who typed "1600 Amph"
     * and tapped away to see the suggestions got Street/City/ZIP fields in
     * place of the box, prefilled with half a street. Prod, 30 days to
     * 2026-10-26: 24 lookups failed that way while the buyer was on the page,
     * all on phones, and none of those buyers ever got a shipping quote.
     */
    const parseAddressFromText = useCallback(async (addressText: string, via: 'typed' | 'autofill' | 'blur' = 'typed') => {
      const text = addressText.trim();
      // A geocode is a round trip; a suggestion the buyer clicked can commit
      // while it is in flight, and the two rarely resolve to the same place.
      if (!text || hasCommittedAddress() || resolvingRef.current === text) return;
      resolvingRef.current = text;

      try {
        if (isGeocoderReady()) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            const place = await geocodeShippingAddress(geocoder, text);
            if (!place || hasCommittedAddress()) return;
            const fields = addressFromComponents(place.address_components);
            if (fields.line1 && fields.city && fields.state && fields.postal_code) commitAddress(fields, via === 'autofill' ? 'autofill_lookup' : 'typed_geocode');
            return;
          } catch (error) {
            // A throwing geocoder is no better than a missing one: the server can still answer.
            // ZERO_RESULTS is text it cannot place, not a fault; anything else is logged.
            if ((error as { code?: string } | null)?.code !== 'ZERO_RESULTS') {
              trackError(error instanceof Error ? error : String(error), undefined, { component: 'GuestAddressAutocomplete', action: 'parseAddressFromText' });
            }
          }
        }

        const resolved = resolveTypedAddress ? await resolveTypedAddress(text) : null;
        if (hasCommittedAddress()) return;
        if (resolved) {
          commitAddress(resolved, via === 'autofill' ? 'autofill_lookup' : 'typed_server');
        } else if (via !== 'blur' || !isGeocoderReady()) {
          // On a blur with Maps loaded the box stays: its suggestions are
          // still there, and so is the "enter it manually" link.
          openManualEntry(text, 'lookup_failed', { via, maps_ready: isGeocoderReady(), ...typedAddressShape(text) });
        }
      } finally {
        resolvingRef.current = null;
      }
    }, [hasCommittedAddress, commitAddress, openManualEntry, resolveTypedAddress]);

    const handlePlaceChanged = useCallback(() => {
      const place = autocompleteRef.current?.getPlace();
      if (!place || !place.address_components) return;
      commitAddress(addressFromComponents(place.address_components), 'suggestion');
    }, [commitAddress]);

    /*
     * The handlers, held still.
     *
     * `handlePlaceChanged` and `parseAddressFromText` both depend on
     * `commitAddress`, which changes identity whenever the address state does
     * -- so listing them as effect dependencies rebuilt the whole Places widget
     * on the way through typing an address. Google appends a fresh
     * `.pac-container` to <body> each time it constructs one and removes none,
     * which is how a page ended up carrying six of them with the live
     * suggestions landing underneath a stale one.
     *
     * Refs keep the listeners pointing at the current closures while the effect
     * below depends only on whether the input is mounted.
     */
    const handlePlaceChangedRef = useRef(handlePlaceChanged);
    const parseAddressFromTextRef = useRef(parseAddressFromText);
    useEffect(() => {
      handlePlaceChangedRef.current = handlePlaceChanged;
      parseAddressFromTextRef.current = parseAddressFromText;
    }, [handlePlaceChanged, parseAddressFromText]);

    useEffect(() => {
      // `manualEntry` HAS TO BE IN THE CONDITION AND IN THE DEPS.
      //
      // The input is rendered by `isEditingAddress && !manualEntry`, but this
      // effect used to watch only the first of those. Opening the manual fields
      // unmounts the input WITHOUT changing `isEditingAddress`, so the effect
      // did not re-run and never cleaned up; closing them mounts a brand-new
      // input element, and because the effect still did not re-run, nothing
      // attached Places to it. The box came back looking normal and did
      // nothing at all -- no suggestions, no commit on Enter.
      //
      // It could not happen before the link existed: manual entry was a
      // one-way door, so nothing ever came back to this input.
      if (!isEditingAddress || manualEntry) return;
      /*
       * Built now, or on a later retry once Maps arrives -- the usual case, since
       * Maps loads `lazyOnload`. The teardown used to be whatever the FIRST
       * attempt returned, which on a retry was nothing: a widget built late was
       * never torn down, so its listeners and its `.pac-container` outlived the
       * box, and a retry loop started before the fields opened kept running.
       */
      let teardown: (() => void) | undefined;
      let retry: ReturnType<typeof setTimeout> | undefined;
      const initAutocomplete = () => {
        if (typeof window.google !== 'undefined' && window.google?.maps?.places && inputRef.current) {
          const containersBefore = new Set(document.querySelectorAll('.pac-container'));

          autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
            fields: ['address_components', 'geometry'],
            types: ['address'],
            componentRestrictions: { country: ['us', 'ca'] },
          });

          autocompleteRef.current.addListener('place_changed', () => handlePlaceChangedRef.current());

          const inputElement = inputRef.current;

          const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();

              const place = autocompleteRef.current?.getPlace();
              if (place && place.address_components) {
                handlePlaceChangedRef.current();
              } else {
                const addressText = (e.target as HTMLInputElement).value;
                void parseAddressFromTextRef.current(addressText);
              }
            }
          };

          // Autofill and paste put a whole address in with one input event;
          // typing adds a character at a time. This used to run on every
          // keystroke past ten characters, so a buyer typing "1600
          // Amphitheatre Parkway" had "1600 Amphit" looked up mid-word, and
          // when that failed the street/city/ZIP fields opened on it while
          // they were still typing (seen in the guest-checkout guide video).
          // A length jump alone let phone keyboards through: tapping a word
          // suggestion adds the whole word at once. isAutofillInput also reads
          // the event's inputType and wants more than one word.
          let lastValue = inputElement.value;
          const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            const prev = lastValue;
            lastValue = target.value;
            if (!isAutofillInput(e, prev, target.value)) return;

            setTimeout(() => {
              const value = target.value;
              if (value && value.length > MIN_ADDRESS_LENGTH && !shippingAddressRef.current.line1) {
                void parseAddressFromTextRef.current(value, 'autofill');
              }
            }, AUTOFILL_DETECTION_DELAY_MS);
          };

          inputElement.addEventListener('keydown', handleKeyDown);
          inputElement.addEventListener('input', handleInput);

          teardown = () => {
            inputElement.removeEventListener('keydown', handleKeyDown);
            inputElement.removeEventListener('input', handleInput);

            /*
             * TEAR THE WIDGET DOWN, NOT JUST THE LISTENERS.
             *
             * Google renders its suggestion list in a `.pac-container` it
             * appends to <body>, outside React's tree, and it never removes
             * one. Every trip through the manual fields builds another
             * Autocomplete and leaves the previous container behind: after two
             * round trips the page carried FOUR of them, stacked at the same
             * position, and which one a buyer sees is whichever happens to be
             * on top. The suggestions were being produced the whole time and
             * landing in a dead node -- the box looked broken.
             *
             * Detaching the instance's listeners is not enough on its own,
             * because the container is DOM, not a listener. Both have to go.
             */
            window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
            autocompleteRef.current = null;
            // Only the containers that appeared while THIS widget was alive.
            // Removing every `.pac-container` on the page also removed the one
            // belonging to a widget that had just been built, so the box went
            // quiet: suggestions with nowhere to render.
            document.querySelectorAll('.pac-container').forEach((node) => {
              if (!containersBefore.has(node)) node.remove();
            });
          };
        } else {
          retry = setTimeout(initAutocomplete, AUTOCOMPLETE_INIT_RETRY_DELAY_MS);
        }
      };

      initAutocomplete();
      if (focusOnOpenRef.current) setTimeout(() => {
        // preventScroll: focusing for the user must not move the page. On
        // /sell the box mounts when Choose Shipping opens the step, and the
        // focus's own scroll cut short the smooth scroll that brings the
        // step into view, leaving it half below the fold.
        inputRef.current?.focus({ preventScroll: true });
      }, INPUT_FOCUS_DELAY_MS);

      return () => {
        clearTimeout(retry);
        teardown?.();
      };
    }, [isEditingAddress, manualEntry]);

    /**
     * Commit whatever is typed in the address box when the buyer leaves it.
     *
     * Only Enter and clicking a Google suggestion used to commit an address. A
     * buyer who typed their address and then moved on to another field -- the
     * name, most often, since it sits right above -- left `line1` empty, and the
     * continue button then failed validation without saying anything.
     *
     * Leaving the TAB is a blur too, and it is not leaving the box. 5 of the 20
     * failed lookups above ran within a second of the page going hidden: a buyer
     * who switched apps mid-address came back to Street/City/ZIP fields holding
     * half a street. The box keeps its text and focus comes back to it, so the
     * blur that really leaves it still commits.
     */
    const handleAddressBlur = () => {
      setAddressFocus(false);
      const typed = inputRef.current?.value || '';
      if (!typed.trim() || hasCommittedAddress()) return;

      setTimeout(() => {
        if (hasCommittedAddress() || document.visibilityState === 'hidden' || !document.hasFocus()) return;
        void parseAddressFromText(typed, 'blur');
      }, BLUR_COMMIT_DELAY_MS);
    };

    /**
     * Enter without Maps.
     *
     * The keydown listener that commits on Enter is attached only once the
     * Autocomplete exists, so without Maps, Enter did nothing at all.
     */
    const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter' || autocompleteRef.current) return;
      e.preventDefault();
      void parseAddressFromText(e.currentTarget.value);
    };

    /** Commit once the buyer leaves a field with the whole address filled in. */
    const handleManualBlur = () => {
      if (!hasCommittedAddress()) commitManualAddress(manualFields, false);
    };

    const handleEditAddress = () => {
      if (tracksCheckoutRef.current) trackAddressEvent('address_change_clicked', {});
      focusOnOpenRef.current = true;
      setIsEditingAddress(true);
      setAddressFocus(true);
      // Clear the street address. The apt line stays: it is the buyer's own text,
      // not part of the street address they came back to correct, and the line2
      // input keeps its state across this edit either way.
      pushAddress({
        ...shippingAddressRef.current,
        line1: '',
        city: '',
        state: '',
        postal_code: '',
      });
      // Focus the input after a brief delay
      setTimeout(() => {
        inputRef.current?.focus();
      }, INPUT_FOCUS_DELAY_MS);
    };

    // Update name in parent
    useEffect(() => {
      pushAddressField('name', nameState.name);
    }, [nameState.name, shippingAddress, pushAddressField]);

    // Update line2 in parent
    useEffect(() => {
      pushAddressField('line2', line2State.line2);
    }, [line2State.line2, shippingAddress, pushAddressField]);

    /**
     * Apt / suite / unit, defined once and placed twice.
     *
     * Both paths need it and it carries the same state either way -- it syncs
     * to the parent through its own effect above, independent of how the street
     * address was arrived at. Declaring it here rather than copying the JSX
     * keeps the manual form and the lookup form from drifting apart, and the
     * two render sites are mutually exclusive so only one is ever mounted.
     */
    const line2Field = (
      <div className="mb-1">
        <FormInput
          validate={undefined}
          id="line2"
          onFocus={setLine2Focus}
          min=""
          autocomplete="shipping address-line2"
          setState={setLine2State}
          label="Apt, suite, unit, c/o (optional)"
          state={line2State}
          errors={line2Errors}
          focus={line2Focus}
          filled={line2Filled}
          setFilled={setLine2Filled}
          setErrors={setLine2Errors}
          errorMessages={null}
        />
      </div>
    );

    // The recipient's name: under Contact after the address, or the first field
    // of Delivery on the one-step page (`nameFirst`). One field either way.
    const nameField = (
      <div className="mb-1">
        <FormInput
          id="name"
          data-testid="name-input"
          onFocus={(f) => {
            setNameFocus(f);
            if (tracksCheckout) trackCheckoutFieldFocus('guest_name', GUEST_FIELD_CONTEXT);
          }}
          onBlur={(fields) => {
            if (!tracksCheckout) return;
            // Length, never the name itself -- see utils/checkout-field-tracking.
            const value = String((fields as Record<string, unknown>)?.name ?? '').trim();
            trackCheckoutFieldBlur('guest_name', GUEST_FIELD_CONTEXT, {
              filled: value !== '',
              length: value.length,
            });
          }}
          min=""
          autocomplete="shipping name"
          validate={() => checkName(nameState, nameErrors, setNameErrors, 'name')}
          setState={setNameState}
          label={nameLabel}
          state={nameState}
          errors={nameErrors}
          focus={nameFocus}
          filled={nameFilled}
          setFilled={setNameFilled}
          setErrors={setNameErrors}
          errorMessages={
            <>
              <ErrorMessage
                cls="name-invalid-error"
                msg={nameInvalidErrorMessage}
              />
              <ErrorMessage
                cls="lastName-needed-error"
                msg={lastNameNeededErrorMessage}
              />
            </>
          }
        />
      </div>
    );

    // The same bars the fields hold themselves to: checkName's two characters
    // and the email check validateGuestCheckout runs. Never with no email
    // field, since then there is nothing to show beside the name.
    const contactFolded =
      !contactActive &&
      !!emailField &&
      nameState.name.trim().length >= 2 &&
      isValidEmail(contactEmail.trim());

    return (
      <div className="guest-address-autocomplete" data-testid="guest-address-autocomplete">
        {nameFirst && (nameSlot ? createPortal(nameField, nameSlot) : nameField)}
        {isEditingAddress && manualEntry ? (
          <div className="guest-address-manual" data-testid="guest-address-manual">
            {/*
              * NOT AN APOLOGY. This said "We couldn't look that address up",
              * which was true when the only way in was a failed lookup and is a
              * lie now that a buyer can choose to type: it blames the lookup for
              * something they asked for. The way back matters for the same
              * reason -- manual entry used to be a one-way door because there
              * was nothing to go back to.
              */}
            <div className="guest-address-manual-head">
              <p className="guest-address-manual-note">Enter your address</p>
              <button
                type="button"
                className="guest-address-manual-link highlight"
                data-testid="close-manual-entry"
                onClick={closeManualEntry}
              >
                Use address lookup
              </button>
            </div>
            {([
              ['manualLine1', 'Street address', 'shipping address-line1', 'Enter your street address'],
              ['manualCity', 'City', 'shipping address-level2', 'Enter your city'],
            ] as const).map(([id, label, autocomplete, message]) => (
              <div className="mb-1" key={id}>
                <FormInput
                  validate={undefined}
                  id={id}
                  onFocus={setManualFocus}
                  onBlur={handleManualBlur}
                  min=""
                  autocomplete={autocomplete}
                  setState={setManualFields}
                  label={label}
                  state={manualFields}
                  errors={manualErrors}
                  focus={manualFocus}
                  filled={manualFilled}
                  setFilled={setManualFilled}
                  setErrors={setManualErrors}
                  errorMessages={<ErrorMessage cls={`${id}-error`} msg={message} />}
                />
              </div>
            ))}
            {/*
              * Apt/suite belongs HERE, with the rest of the address.
              * It used to render only on the lookup path, after a base address
              * came back -- so a buyer typing their address manually had
              * nowhere to put their unit number until they had finished and
              * committed, if they found it at all. It is the same field and the
              * same state either way; only where it sits changes.
              */}
            {line2Field}
            {/*
              * Country, region and postcode read as one line on a form and are
              * three short values, so they get one row. They wrap back to full
              * width on a narrow screen, where three controls across is worse
              * than a little scrolling.
              */}
            <div className="guest-address-manual-row">
            <div className="mb-1">
              <FormSelect
                onBlur={undefined}
                onChange={undefined}
                validate={undefined}
                id="manualCountry"
                realName="country"
                autocomplete="shipping country"
                options={COUNTRY_OPTIONS.map((c) => ({ id: c.value, name: c.label }))}
                onFocus={setManualFocus}
                setState={setManualFields}
                label="Country"
                state={manualFields}
                errors={manualErrors}
                focus={manualFocus}
                filled={manualFilled}
                setFilled={setManualFilled}
                setErrors={setManualErrors}
                errorMessages={<ErrorMessage cls="manualCountry-error" msg="Choose your country" />}
              />
            </div>
            <div className="mb-1">
              <FormSelect
                onBlur={undefined}
                onChange={undefined}
                validate={undefined}
                id="manualState"
                realName="state"
                // The browser fills a state select from its saved address by
                // this name; it matches the code ("OR") or the name ("Oregon").
                autocomplete="shipping address-level1"
                options={regionOptionsFor(manualFields.manualCountry)}
                onFocus={setManualFocus}
                setState={setManualFields}
                label={manualFields.manualCountry === 'CA' ? 'Province' : 'State'}
                state={manualFields}
                errors={manualErrors}
                focus={manualFocus}
                filled={manualFilled}
                setFilled={setManualFilled}
                setErrors={setManualErrors}
                errorMessages={<ErrorMessage cls="manualState-error" msg={manualFields.manualCountry === 'CA' ? 'Choose your province' : 'Choose your state'} />}
              />
            </div>
            <div className="mb-1">
              <FormInput
                validate={undefined}
                id="manualZip"
                onFocus={setManualFocus}
                onBlur={handleManualBlur}
                min=""
                inputMode={manualFields.manualCountry === 'CA' ? 'text' : 'numeric'}
                autocomplete="shipping postal-code"
                setState={setManualFields}
                label={manualFields.manualCountry === 'CA' ? 'Postal code' : 'ZIP code'}
                state={manualFields}
                errors={manualErrors}
                focus={manualFocus}
                filled={manualFilled}
                setFilled={setManualFilled}
                setErrors={setManualErrors}
                errorMessages={<ErrorMessage cls="manualZip-error" msg={manualFields.manualCountry === 'CA' ? 'Enter a postal code like A1A 1A1' : 'Enter a 5-digit ZIP code'} />}
              />
            </div>
            </div>
          </div>
        ) : (
        <div className="mb-1">
          <label
            className={`input-label input-label-address ${addressFocus ? 'focus' : ''} ${shippingAddress.line1 ? 'filled' : ''} ${addressError ? 'invalid STATE--address-missing' : ''}`}
            htmlFor="address-input"
          >
            {isEditingAddress ? (
              <input
                ref={inputRef}
                type="text"
                id="address-input"
                data-testid="address-autocomplete-input"
                className="form-input bordered google-autocomplete"
                placeholder="Start typing your address"
                autoComplete="off"
                onFocus={() => setAddressFocus(true)}
                onBlur={handleAddressBlur}
                onKeyDown={handleAddressKeyDown}
              />
            ) : (
              <div className="address defaultAddress" data-testid="selected-address-display">
                <div className="text">
                  <span className="name">
                    {shippingAddress.name && (
                      <>
                        {shippingAddress.name}
                        <br />
                      </>
                    )}
                  </span>
                  <span className="highlight">
                    {shippingAddress.line1}
                    <br />
                    {shippingAddress.line2 && (
                      <>
                        {shippingAddress.line2}
                        <br />
                      </>
                    )}
                    {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                    {/* The country only when it is not the one nearly every order
                        ships to. US and Canada are the only two that ship. */}
                    {shippingAddress.country === 'CA' && (
                      <>
                        <br />
                        Canada
                      </>
                    )}
                  </span>
                </div>
                {/* A word, not a bare ✕: the ✕ read as "delete my address", and a
                    buyer who only wanted to fix a typo had to guess that it
                    reopened the lookup. */}
                <button
                  type="button"
                  className="change-address-button highlight"
                  data-testid="edit-address-button"
                  onClick={handleEditAddress}
                >
                  Change
                </button>
              </div>
            )}
            <ErrorMessage
              cls="address-missing-error"
              msg={addressMissingErrorMessage}
            />
            <div className="valid-check" />
          </label>
          {/*
            * The way out of the lookup, offered rather than waited for.
            *
            * Manual entry only appeared after BOTH geocoders failed, which
            * meant a buyer whose address Maps placed wrongly -- or who is on a
            * rural route, a new build, or an address Google simply has not got
            * -- had nothing to press. They could see the suggestions were wrong
            * and still not tell us where they live.
            */}
          {/* Only while looking up. Once an address is chosen its card's
              Change button is the way back, and it reopens the lookup with
              this link under it; showing both put a second "fix it" control
              between the address and its apt line. */}
          {!manualEntry && isEditingAddress && (
            <button
              type="button"
              className="guest-address-manual-link highlight"
              data-testid="enter-address-manually"
              onClick={() => openManualEntry(inputRef.current?.value || '', 'buyer')}
            >
              Can&apos;t find it? Enter it manually
            </button>
          )}
        </div>
        )}

        {/*
          * The lookup path's slot for the same field: after a base address has
          * come back, since there is nothing to add a unit to before that.
          * Rendered from the SAME `line2Field` as the manual form so the two
          * can never drift apart, and guarded so only one of them is ever in
          * the DOM -- two inputs sharing id="line2" would break the label
          * association and hand Playwright a strict-mode violation.
          */}
        {!manualEntry && !isEditingAddress && shippingAddress.line1 && line2Field}

        {/*
          * WHO IT GOES TO, AFTER WHERE.
          *
          * The address is what prices the order: shipping cannot be quoted
          * without it, and the summary reads "Enter Shipping Address" until it
          * is in. So it comes first, and the name and email -- which buy the
          * buyer nothing on screen -- are asked right after, together, the way
          * most checkouts ask for contact details. The email used to sit
          * between the name and the address; the two are still side by side.
          */}
        {/*
          * FOLDED TO ONE LINE once both are in and the buyer has moved on.
          *
          * A filled name and email are two tall fields that ask nothing more,
          * and they sat between the address and the shipping methods -- on a
          * laptop they pushed the methods below the fold. The fields stay
          * mounted, only hidden: the email's state and validation live in
          * checkout-shared, and an autofilled value must not be dropped. Focus
          * inside, or Change, opens it again; it folds when focus leaves.
          */}
        {!nameFirst && <div
          ref={contactRef}
          className={`guest-contact ${contactFolded ? 'STATE--folded' : ''}`}
          data-testid="guest-contact"
          onFocus={() => setContactActive(true)}
          onBlur={(e) => {
            // A node that unmounts while focused (the Change button) blurs on
            // its way out; that is not the buyer leaving.
            if (!e.target.isConnected) return;
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setContactActive(false);
          }}
        >
          {contactFolded ? (
            <div className="contact-summary" data-testid="contact-summary">
              <div className="contact-summary-text">
                <span className="name">{nameState.name.trim()}</span>
                <span className="highlight">{contactEmail.trim()}</span>
              </div>
              <button
                type="button"
                className="change-address-button highlight"
                data-testid="edit-contact-button"
                onClick={() => {
                  setContactActive(true);
                  setTimeout(() => {
                    contactRef.current?.querySelector<HTMLInputElement>('input')?.focus();
                  }, 0);
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <h3 className="guest-subheading">Contact</h3>
          )}
          <div className={contactFolded ? 'hidden' : 'guest-contact-fields'} data-testid="contact-fields">
            {nameField}
            {emailField}
          </div>
        </div>}
      </div>
    );
  }
);

GuestAddressAutocomplete.displayName = 'GuestAddressAutocomplete';

export default GuestAddressAutocomplete;
